import React, { useEffect, useRef, useState } from 'react';
import { BadgeCheck, Clock, RotateCw } from 'lucide-react';
import * as SignService from '../../service/sign';

/**
 * Board-settings building blocks, lifted out of the envelope builder's step 3.
 *
 * These moved because what they configure moved. A verified sender address is
 * a property of the BOARD now — everyone on it can use one, and it is chosen
 * once rather than per request — so the controls belong to the Settings tab
 * and not to a screen you walk through on the way to sending something.
 *
 * Nothing here is rendered yet: the Settings tab is the piece still to build.
 * They are preserved verbatim rather than deleted with step 3, because the
 * verification loop they drive is subtle (token rotation, pending vs verified,
 * resend) and re-deriving it from the endpoints would be a waste.
 *
 * `SenderSection` still takes `selectedId`/`onSelect`: on the Settings tab that
 * pair means "which address this BOARD sends as", where it used to mean "which
 * address this request sends as".
 */

export function Card({ icon: Icon, title, children }) {
  return (
    <section className='bg-white border border-gray-200 rounded-xl p-4'>
      <h3 className='flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3'>
        <Icon size={15} className='text-gray-400' />
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * A labelled switch — the identity and notification cards both need one.
 *
 * `help` sits under the title rather than beside the switch: these settings all
 * have a consequence worth a sentence, and a sentence squeezed next to a 36px
 * control gets cut instead of read.
 */
export function Toggle({ on, onChange, title, help, disabled = false }) {
  return (
    <div className={`flex items-start justify-between gap-4 ${disabled ? 'opacity-60' : ''}`}>
      <div className='min-w-0'>
        <div className='text-sm font-medium text-gray-900'>{title}</div>
        {help && <p className='text-xs text-gray-500 mt-0.5 max-w-md'>{help}</p>}
      </div>
      <button
        type='button'
        role='switch'
        aria-checked={on}
        aria-label={title}
        disabled={disabled}
        onClick={() => !disabled && onChange(!on)}
        className={`relative inline-flex flex-shrink-0 h-5 w-9 rounded-full transition-colors mt-0.5 ${
          on ? 'bg-blue-600' : 'bg-gray-300'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
            on ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Your sender addresses, and which one this request goes out as.
 *
 * Was a single account-wide row shown as one line with an Edit button. That row
 * was everyone's: it showed you a colleague's address, and saving your own
 * replaced theirs. This lists only addresses you verified, and adding one adds
 * a row rather than overwriting anybody.
 *
 * A radio list rather than a dropdown. It is short, and every option carries a
 * status the reader needs at once — a pending address is the one thing they
 * have to act on, and a collapsed control hides exactly that.
 */
/**
 * Poll while an address is waiting to be verified.
 *
 * Verification happens SOMEWHERE ELSE — another tab, usually another device —
 * so this screen cannot know it happened except by asking again. Asking on a
 * timer means the row turns green on its own, which is the outcome; a refresh
 * button would only be a way of asking the user to do the polling by hand.
 *
 * Bounded, and the bound is the interesting part. Two minutes of *visible*
 * polling, restarted every time the tab becomes visible again — because coming
 * back to this tab IS the moment someone returns from clicking the link. That
 * restart is what makes a manual control unnecessary: the gesture that would
 * have preceded pressing it already triggers the check.
 *
 * Paused rather than counted while hidden, so the common case — off reading the
 * inbox in another tab — costs nothing until they come back, and does not burn
 * the window while nobody is looking.
 */
const POLL_EVERY_MS = 5000;
const POLL_WINDOW_MS = 2 * 60 * 1000;

function usePendingPoll(hasPending, onReload) {
  const [polling, setPolling] = useState(false);
  // Held in a ref: the parent rebuilds this callback on every render, and
  // depending on it would tear down and restart the interval each time.
  const reloadRef = useRef(onReload);
  reloadRef.current = onReload;

  const [windowStartedAt, setWindowStartedAt] = useState(() => Date.now());
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) setWindowStartedAt(Date.now());
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (!hasPending) {
      setPolling(false);
      return undefined;
    }
    setPolling(true);
    const id = setInterval(() => {
      if (document.hidden) return;
      if (Date.now() - windowStartedAt > POLL_WINDOW_MS) {
        clearInterval(id);
        setPolling(false);
        return;
      }
      reloadRef.current?.();
    }, POLL_EVERY_MS);
    return () => clearInterval(id);
  }, [hasPending, windowStartedAt]);

  return polling;
}

export function SenderSection({ getSessionToken, boardId, senders, selectedId, onSelect, onReload }) {
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const list = senders || [];
  const polling = usePendingPoll(
    list.some((entry) => entry.status !== 'verified'),
    onReload,
  );

  const openAdd = () => {
    setEmail('');
    setDisplayName('');
    setNotice(null);
    setAdding(true);
  };

  const save = async () => {
    if (busy || !email.trim()) return;
    setBusy(true);
    setNotice(null);
    try {
      const token = await getSessionToken();
      const res = await SignService.saveSender(token, boardId, email.trim(), displayName.trim());
      if (res?.ok) {
        await onReload();
        setAdding(false);
        // Deliberately not selected. It cannot send anything until it is
        // verified, and pointing the request at a pending address would mean the
        // send gate blocks on the thing the user thinks they just finished.
        setNotice(
          res.verificationSent
            ? { tone: 'ok', text: `Verification email sent to ${res.sender.email}` }
            : { tone: 'ok', text: 'Saved.' },
        );
      } else {
        setNotice({ tone: 'warn', text: res?.message || 'Could not save the address.' });
      }
    } catch (err) {
      console.error('Failed to save sender', err);
      setNotice({ tone: 'warn', text: 'Could not save the address.' });
    } finally {
      setBusy(false);
    }
  };

  const resend = async (id) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const token = await getSessionToken();
      const res = await SignService.resendSenderVerification(token, boardId, id);
      if (res?.ok) {
        await onReload();
        setNotice({ tone: 'ok', text: 'Verification email re-sent.' });
      } else {
        setNotice({ tone: 'warn', text: 'Could not resend the email.' });
      }
    } catch (err) {
      console.error('Failed to resend verification', err);
      setNotice({ tone: 'warn', text: 'Could not resend the email.' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const token = await getSessionToken();
      const res = await SignService.removeSender(token, boardId, id);
      if (res?.ok) {
        // Cleared first: a request still pointing at a row that no longer exists
        // reads as "sending as that address" right up until the send.
        if (selectedId === id) onSelect(null);
        await onReload();
      } else {
        setNotice({ tone: 'warn', text: 'Could not remove the address.' });
      }
    } catch (err) {
      console.error('Failed to remove sender', err);
      setNotice({ tone: 'warn', text: 'Could not remove the address.' });
    } finally {
      setBusy(false);
    }
  };

  if (adding) {
    return (
      <div className='px-3 py-3 border border-blue-200 rounded-lg bg-gray-50 space-y-2'>
        <div className='grid grid-cols-2 gap-2'>
          <div>
            <label className='block text-[11px] text-gray-500 mb-0.5'>Sender email</label>
            <input
              type='email'
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder='you@company.com'
              className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
            />
          </div>
          <div>
            <label className='block text-[11px] text-gray-500 mb-0.5'>Display name</label>
            <input
              type='text'
              value={displayName}
              maxLength={255}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder='e.g. Acme Sales'
              className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none'
            />
          </div>
        </div>

        <p className='text-[11px] text-gray-400'>
          Please go to your mail box and verify this email, and refresh the page to move forward. 
        </p>

        {notice && (
          <p className={`text-xs ${notice.tone === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{notice.text}</p>
        )}

        <div className='flex items-center gap-3 pt-0.5'>
          <button
            onClick={save}
            disabled={busy || !email.trim()}
            className='flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50'
          >
            {busy && <RotateCw size={11} className='animate-spin' />}
            Save and verify
          </button>
          <button onClick={() => setAdding(false)} className='text-xs font-medium text-gray-500'>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='px-3 py-2.5 border border-gray-100 rounded-lg bg-gray-50 space-y-1.5'>
      {senders === undefined ? (
        <span className='text-sm text-gray-400'>Loading your sender addresses…</span>
      ) : (
        <>
          {/* The app default is a real choice, not the absence of one, so it is a
              row you pick rather than what you get by picking nothing.

              Its radio appears only once there is something to pick BETWEEN: a
              radio group of one is not a choice, it is a control that cannot be
              operated, and with no addresses of your own the line only has to
              state what will happen. */}
          <SenderChoice
            checked={selectedId === null}
            onSelect={() => onSelect(null)}
            label='Default — emails go out as SmartSign'
            muted
            showRadio={list.length > 0}
            actions={
              <button onClick={openAdd} className='text-xs font-medium text-blue-600 flex-shrink-0'>
                Add an address
              </button>
            }
          />

          {list.map((entry) => (
            <SenderChoice
              key={entry.id}
              checked={selectedId === entry.id}
              onSelect={() => onSelect(entry.id)}
              disabled={entry.status !== 'verified'}
              label={`${entry.displayName ? `${entry.displayName} · ` : ''}${entry.email}`}
              badge={
                entry.status === 'verified' ? (
                  <span className='inline-flex items-center gap-0.5 text-[11px] text-green-700 flex-shrink-0'>
                    <BadgeCheck size={12} /> Verified
                  </span>
                ) : (
                  <span className='inline-flex items-center gap-0.5 text-[11px] text-amber-600 flex-shrink-0'>
                    <Clock size={12} /> Pending
                  </span>
                )
              }
              actions={
                <>
                  {entry.status !== 'verified' && (
                    <button
                      onClick={() => resend(entry.id)}
                      disabled={busy}
                      className='text-xs font-medium text-blue-600 flex-shrink-0 disabled:opacity-50'
                    >
                      Resend
                    </button>
                  )}
                  <button
                    onClick={() => remove(entry.id)}
                    disabled={busy}
                    className='text-xs font-medium text-gray-400 hover:text-red-600 flex-shrink-0 disabled:opacity-50'
                  >
                    Remove
                  </button>
                </>
              }
            />
          ))}

        </>
      )}

      {notice && (
        <p className={`text-xs ${notice.tone === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{notice.text}</p>
      )}

      {/*
        One quiet line under the list rather than a spinner on the row. The row
        would jitter every five seconds, and the thing worth saying is not "a
        request is in flight" — it is "you do not have to reload", which is the
        belief that sends people to the browser's refresh button.
      */}
      {polling && (
        <p className='flex items-center gap-1.5 text-xs text-gray-400'>
          <RotateCw size={11} className='animate-spin flex-shrink-0' />
          Watching for verification.
        </p>
      )}
    </div>
  );
}

/** One row of the sender picker: choice, identity, status, and its own actions. */
function SenderChoice({
  checked,
  onSelect,
  label,
  badge,
  actions,
  disabled = false,
  muted = false,
  showRadio = true,
}) {
  const text = (
    <span className={`text-sm truncate ${muted ? 'text-gray-500' : 'text-gray-900'} ${disabled ? 'opacity-60' : ''}`}>
      {label}
    </span>
  );

  return (
    <div className='flex items-center gap-2.5 min-w-0'>
      {/* label wraps only the choosing part — the actions beside it are buttons,
          and a button inside a label fires the label's control as well.
          Without a radio there is nothing to label, so the text stands alone. */}
      {showRadio ? (
        <label className={`flex items-center gap-2.5 min-w-0 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}>
          <input
            type='radio'
            name='sign-sender'
            checked={checked}
            disabled={disabled}
            onChange={onSelect}
            className='flex-shrink-0'
          />
          {text}
        </label>
      ) : (
        text
      )}
      {badge}
      <span className='flex-1' />
      {actions}
    </div>
  );
}

